"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useLocation, useNavigate, useSearchParams, Link } from "react-router-dom";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { getInvoice, refreshCoinbaseOrderStatus } from "../services/authapi";
import { CoinbaseHeadlessOnrampModal } from "./CoinbaseHeadlessOnrampModal";
import QRCode from "qrcode";
import toast, { Toaster } from "react-hot-toast";
import {
  Copy, Check, ExternalLink, Download, CheckCircle2,
  Zap, Wallet, CreditCard, RefreshCw, ArrowLeft, Clock,
  Shield, AlertCircle, Loader2, QrCode, Radio, Smartphone, X,
} from "lucide-react";
import { SiCashapp } from "react-icons/si";
import { peerExtensionSdk } from "@zkp2p/sdk";
import StyledQR from "./StyledQR";

// ─── Types ────────────────────────────────────────────────────────────────────

interface InvoiceData {
  invoiceId: string;
  buyerEmail?: string;
  sellerWallet?: string;
  sellerUsername?: string | null;
  amount: number;
  token: string;
  chain: string;
  source?: string;
  paymentMethod?: string;
  collectSlug?: string | null;
  dedicatedWalletAddress: string;
  status: "pending" | "paid" | "completed" | "expired" | "late_paid" | "flagged";
  expiresAt: number | null;
  paidAt?: number | null;
  sweepTxHash?: string | null;
  incomingTxHash?: string | null;
}

interface NavState {
  method: string;
  amount: number;
  dedicatedAddress: string;
  buyerEmail: string;
  sellerName?: string;
  sellerPhoto?: string;
  source?: string;
  sellerWallet?: string;
}

interface LightningOrder {
  orderId: string;
  token: string;
  invoice: string;
  btcAmount: string;
  usdcAmount: string;
  expiresAt: number;
}

interface SellerProfile {
  username: string;
  photoURL: string | null;
  themeColor: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SOURCE_LABELS: Record<string, string> = {
  request: "Payment Request",
  donation: "Donation",
  buymeCoffee: "Buy Me a Coffee",
  manual: "Invoice Payment",
};
const SOURCE_ICONS: Record<string, string> = {
  request: "💳", donation: "💝", buymeCoffee: "☕", manual: "📄",
};
// `lightning-btc` and `lightning-cashapp` both ride the same Lightning
// rail, but the buyer-facing experience is intentionally different:
//   • lightning-btc       → "Add Money via Lightning" — generic Lightning
//                           Invoice flow, forced to FixedFloat with a
//                           "Powered by ff.io" attribution chip.
//   • lightning-cashapp   → "CashApp Pay" — CashApp-branded flow, no
//                           ff.io attribution (uses the default FF-primary
//                           routing with SimpleSwap fallback).
// They share the same render branch (QR + bolt11 + "Pay with CashApp Pay"
// CTA + consent checkbox); the only divergence is the FF call options
// and the ff.io chip — both gated on `method === "lightning-btc"`.
const METHOD_LABELS: Record<string, string> = {
  "coinbase-onramp": "Coinbase",
  "lightning-btc": "Add Money via Lightning",
  "lightning-cashapp": "CashApp Pay",
  cashapp: "Wallet Apps",
  wallet: "Wallet Transfer",
  "card-bank": "Card / Bank",
};
const METHOD_ICON: Record<string, React.ReactNode> = {
  "coinbase-onramp": null,
  "lightning-btc": <Zap className="w-5 h-5 text-[#61CE70]" />,
  "lightning-cashapp": <SiCashapp className="w-5 h-5 text-[#00D632]" />,
  cashapp: <Wallet className="w-5 h-5" />,
  wallet: <QrCode className="w-5 h-5" />,
  "card-bank": <CreditCard className="w-5 h-5" />,
};

const REFERRER = "Cashtap";
const REFERRER_LOGO = "https://cashtap.cash/logo.png";
const CALLBACK_URL = "https://cashtap.cash/add-funds?peerSuccess=true";
const BASE_USDC_TOKEN = "8453:0x833589fcd6edb6e08f4c7c32d4f71b54bda02913";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white transition-all duration-200 cursor-pointer">
      {copied ? <Check className="w-3.5 h-3.5 text-[#61CE70]" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied!" : label}
    </button>
  );
}

function CopyInvoiceButton({ invoice }: { invoice: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(invoice).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={copy}
      className="sm:hidden flex items-center justify-center gap-2 w-full py-2.5 rounded-2xl border border-gray-700 hover:border-gray-500 bg-transparent hover:bg-gray-800/40 text-gray-300 hover:text-white transition-all duration-200 cursor-pointer text-xs min-[400px]:text-sm font-medium"
    >
      {copied ? <Check className="w-4 h-4 text-[#61CE70]" /> : <Copy className="w-4 h-4" />}
      {copied ? "Copied!" : "Copy Lightning Invoice"}
    </button>
  );
}

// ─── Status Tracker ───────────────────────────────────────────────────────────

type StepState = "done" | "active" | "pending";
interface StatusStep { label: string; desc: string; state: StepState; }

function StatusTracker({ steps, onRefresh, isPolling, lastChecked }: {
  steps: StatusStep[]; onRefresh: () => void; isPolling: boolean; lastChecked: Date | null;
}) {
  const [tick, setTick] = useState(false);
  // Blink every 1.5s to simulate live feel
  useEffect(() => {
    const t = setInterval(() => setTick(p => !p), 1500);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl overflow-hidden">
      {/* Header */}
      <div className="px-3 sm:px-5 lg:px-4 py-3 sm:py-4 lg:py-2.5 border-b border-gray-700/40 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-2.5">
          <div className="w-6 h-6 sm:w-7 sm:h-7 lg:w-6 lg:h-6 rounded-full bg-[#61CE70]/10 border border-[#61CE70]/30 flex items-center justify-center">
            <Radio className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-3 lg:h-3 text-[#61CE70]" />
          </div>
          <span className="text-xs sm:text-sm lg:text-xs font-semibold text-white">Payment Status</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {lastChecked && (
            <span className="hidden sm:inline text-[10px] text-gray-600 tabular-nums">
              {lastChecked.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
          <div className="flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className={`absolute inline-flex h-full w-full rounded-full bg-[#61CE70] opacity-75 transition-all duration-700 ${tick ? "scale-150 opacity-0" : "scale-100 opacity-75"}`} />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#61CE70]" />
            </span>
            <span className="text-[10px] text-[#61CE70] font-bold uppercase tracking-widest">Live</span>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="px-3 sm:px-5 lg:px-4 py-3 sm:py-5 lg:py-3 space-y-0">
        {steps.map((step, i) => {
          const isLast = i === steps.length - 1;
          const isAwaitingDeposit = step.label === "Awaiting Deposit" && step.state === "active";
          return (
            <div key={i} className="flex gap-3 sm:gap-4 lg:gap-3">
              {/* Icon + line */}
              <div className="flex flex-col items-center">
                <div className={`
                  relative z-10 w-8 h-8 lg:w-6 lg:h-6 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all duration-500
                  ${step.state === "done" ? "bg-[#61CE70]/20 border-[#61CE70]/60"
                    : step.state === "active" ? "bg-amber-500/15 border-amber-400/60"
                    : "bg-gray-800/60 border-gray-700/60"}
                  ${step.state === "active" ? "shadow-[0_0_16px_rgba(251,191,36,0.3)]" : ""}
                `}>
                  {step.state === "done" ? (
                    <Check className="w-3.5 h-3.5 lg:w-3 lg:h-3 text-[#61CE70]" strokeWidth={3} />
                  ) : step.state === "active" ? (
                    /* Blinking dot for active step */
                    <div className={`w-2.5 h-2.5 lg:w-2 lg:h-2 rounded-full bg-amber-400 transition-opacity duration-700 ${tick ? "opacity-30" : "opacity-100"}`} />
                  ) : (
                    <div className="w-2 h-2 lg:w-1.5 lg:h-1.5 rounded-full bg-gray-600" />
                  )}
                </div>
                {!isLast && (
                  <div className={`w-0.5 flex-1 my-1 lg:my-0.5 min-h-[24px] lg:min-h-[12px] rounded-full transition-all duration-700 ${step.state === "done" ? "bg-[#61CE70]/40" : "bg-gray-700/40"}`} />
                )}
              </div>

              {/* Text */}
              <div className={`pt-0.5 min-w-0 ${isLast ? "pb-0" : "pb-3 sm:pb-5 lg:pb-2"}`}>
                <p className={`text-xs sm:text-sm lg:text-xs font-semibold transition-colors duration-300 flex items-center gap-1.5 sm:gap-2 flex-wrap
                  ${step.state === "done" ? "text-white" : step.state === "active" ? "text-amber-300" : "text-gray-600"}`}>
                  {step.label}
                  {step.state === "active" && (
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-medium transition-all duration-500
                      ${isAwaitingDeposit
                        ? tick ? "bg-amber-500/5 border-amber-500/10 text-amber-500/50" : "bg-amber-500/15 border-amber-500/30 text-amber-400"
                        : "bg-amber-500/10 border-amber-500/20 text-amber-400"}`}>
                      <span className={`w-1 h-1 rounded-full bg-amber-400 transition-opacity duration-500 ${tick ? "opacity-0" : "opacity-100"}`} />
                      {isAwaitingDeposit ? "Monitoring…" : "In progress"}
                    </span>
                  )}
                </p>
                <p className={`text-[10px] sm:text-xs lg:text-[10px] mt-0.5 lg:leading-snug transition-colors duration-300
                  ${step.state === "done" ? "text-gray-500" : step.state === "active" ? "text-amber-500/60" : "text-gray-700"}`}>
                  {/* Extra pulse text for awaiting deposit */}
                  {isAwaitingDeposit ? (
                    <span className={`transition-opacity duration-700 ${tick ? "opacity-50" : "opacity-100"}`}>
                      {step.desc}
                    </span>
                  ) : step.desc}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Refresh */}
      <div className="px-3 sm:px-5 lg:px-4 pb-3 sm:pb-5 lg:pb-3">
        <button onClick={onRefresh} disabled={isPolling}
          className="w-full flex items-center justify-center gap-2 py-2.5 lg:py-1.5 rounded-2xl lg:rounded-xl bg-gray-800/50 hover:bg-gray-700/50 disabled:opacity-50 text-gray-400 hover:text-white text-xs lg:text-[11px] font-medium transition-all cursor-pointer border border-gray-700/30">
          <RefreshCw className={`w-3.5 h-3.5 lg:w-3 lg:h-3 ${isPolling ? "animate-spin" : ""}`} />
          {isPolling ? "Checking…" : "Refresh Status"}
        </button>
      </div>
    </div>
  );
}

// ─── Peer Extension Disclaimer Modal ──────────────────────────────────────────

function PeerDisclaimerModal({ onClose, onProceed }: { onClose: () => void; onProceed: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-6 w-full max-w-md shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-amber-400" />
          </div>
          <h3 className="text-base font-bold text-white">Important Note</h3>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Why the popup might not appear</p>
            <ul className="space-y-1.5">
              {[
                "Older browser version (Chrome/Edge should be up to date)",
                "Browser popup blocker or strict privacy settings enabled",
                "Outdated or incorrectly installed Cashtap extension",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400/60 flex-shrink-0 mt-1.5" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">How to fix it</p>
            <ul className="space-y-1.5">
              {[
                "Update your browser to the latest version",
                "Check browser popup settings and whitelist cashtap.cash",
                "Reinstall the Cashtap extension",
                "Clear browser cache and reload",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                  <Check className="w-3.5 h-3.5 text-[#61CE70] flex-shrink-0 mt-0.5" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <p className="text-xs text-gray-600">If you continue to experience issues, please contact our support team.</p>
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 rounded-2xl bg-gray-800 hover:bg-gray-700 text-gray-300 font-semibold text-sm transition-all cursor-pointer">
            Cancel
          </button>
          <button onClick={onProceed} className="flex-1 py-3 rounded-2xl bg-[#61CE70] hover:bg-[#72D981] text-black font-bold text-sm transition-all cursor-pointer">
            I Understand, Proceed
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PaymentInvoicePage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { state } = useLocation() as { state: NavState | null };
  const navigate = useNavigate();
  // `?method=…` survives a hard refresh / shared link in a way that
  // navigate-state doesn't, and overrides whatever Firestore stored
  // (which can lag behind the buyer's actual selection or carry an
  // older method id from a stale invoice doc). Highest-priority
  // source of truth for the payment-method router below.
  const [searchParams] = useSearchParams();

  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [seller, setSeller] = useState<SellerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaid, setIsPaid] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  // Expiry countdown (seconds) — MM:SS only
  const [expiryLeft, setExpiryLeft] = useState<number>(0);

  // Derived: true once the invoice timer has run out (or backend marked it
  // expired). Used to gate auto-init, the Coinbase auto-open countdown,
  // and the rendering of all interactive payment elements.
  const isExpired =
    invoice?.status === "expired" ||
    (invoice?.expiresAt != null && expiryLeft === 0 && !isPaid);

  // Coinbase — Headless Onramp (Apple Pay / Google Pay embedded, card via
  // Coinbase's hosted checkout). Rendered as the "Pay with Visa / MasterCard,
  // Apple Pay or Google Pay" method. The modal handles the whole flow; the
  // invoice's dedicated wallet is the destination and the Firestore listener
  // above is still the source of truth for the paid state.
  const [coinbaseModalOpen, setCoinbaseModalOpen] = useState(false);
  const coinbaseAutoOpened = useRef(false);

  // Lightning
  const [lightningOrder, setLightningOrder] = useState<LightningOrder | null>(null);
  const [lightningLoading, setLightningLoading] = useState(false);
  const [lightningError, setLightningError] = useState<{
    message: string;
    code?: string;
    minAmount?: number;
    eta?: string;
  } | null>(null);

  // Address QR
  const [addressQR, setAddressQR] = useState<string>("");

  // Peer extension (wallet apps)
  const [peerState, setPeerState] = useState<"unknown" | "needs_install" | "needs_connection" | "ready">("unknown");
  const [showPeerDisclaimer, setShowPeerDisclaimer] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [peerProcessing, setPeerProcessing] = useState(false);

  // Buyer must acknowledge the "transfers are irreversible" disclaimer
  // before the "Pay with CashApp Pay" CTA is interactive. Mirrors the
  // gate on the standalone LightningPaymentPage so the wording and
  // behaviour are identical across the two Lightning entry points.
  // This is a regulatory / scam-prevention guard — it stops a buyer
  // tapping through reflexively after being social-engineered.
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Underpayment notice modal — shown once per invoice on first load so the
  // buyer can't miss the "pay the exact amount" warning. After they dismiss
  // it we record the invoice id in localStorage and don't show it again on
  // refresh; the persistent right-rail disclaimer keeps the warning visible.
  const [showUnderpaymentNotice, setShowUnderpaymentNotice] = useState(false);
  const underpaymentStorageKey = invoiceId ? `cashtap.underpaymentNoticeSeen:${invoiceId}` : "";

  const dismissUnderpaymentNotice = useCallback(() => {
    if (underpaymentStorageKey) {
      try { localStorage.setItem(underpaymentStorageKey, "1"); } catch {/* SSR / quota — non-fatal */}
    }
    setShowUnderpaymentNotice(false);
  }, [underpaymentStorageKey]);

  // Polling ref
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const expiryRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Payment-method resolution order:
  //   1. `?method=…` URL query   — set by PaymentFlow's navigate call;
  //                                survives a hard refresh / shared link.
  //   2. navigate-state.method    — set when arriving inline from the
  //                                Collect page in the same tab.
  //   3. invoice.paymentMethod    — Firestore fallback for direct loads
  //                                or stale links.
  //
  // The URL param wins because it's the most explicit and persistent
  // signal of buyer intent — Firestore can store a method id that
  // doesn't yet exist in the renderer (e.g. a new tile id surfaced
  // before the invoice page caught up), in which case the URL param
  // gives the buyer's flow a way to specify the right one.
  //
  // We deliberately do NOT normalize `lightning-cashapp` → `lightning-btc`
  // any more. They're different buyer experiences (see METHOD_LABELS above):
  // identical render path, but `lightning-btc` carries the ff.io attribution
  // and a forced-FF rail, while `lightning-cashapp` doesn't.
  const method =
    searchParams.get("method") ||
    invoice?.paymentMethod ||
    state?.method ||
    "";
  // Convenience boolean used by the render path + the init effect below
  // — true for either Lightning id, lets a single branch handle both.
  const isLightning = method === "lightning-btc" || method === "lightning-cashapp";
  const amount = invoice?.amount ?? state?.amount ?? 0;
  const dedicatedAddress = invoice?.dedicatedWalletAddress || state?.dedicatedAddress || "";
  const buyerEmail = invoice?.buyerEmail || state?.buyerEmail || "";
  const source = invoice?.source || state?.source || "request";
  const pageTitle = SOURCE_LABELS[source] || "Payment";
  const sellerName = seller?.username || state?.sellerName || invoice?.sellerUsername || "Recipient";
  const sellerPhoto = seller?.photoURL || state?.sellerPhoto || null;

  // ─── Firestore real-time listener (primary payment detection) ─────────────

  useEffect(() => {
    if (!invoiceId || isPaid) return;
    const unsub = onSnapshot(doc(db, "paymentInvoices", invoiceId), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() as InvoiceData;
      setInvoice(data);
      setLastChecked(new Date());
      if (data.status === "paid" || data.status === "completed" || data.status === "late_paid") {
        setIsPaid(true);
        clearInterval(pollRef.current!);
      }
    });
    return () => unsub();
  }, [invoiceId, isPaid]);

  // ─── Initial invoice fetch ────────────────────────────────────────────────

  useEffect(() => {
    if (!invoiceId) { setError("Invalid invoice link."); setLoading(false); return; }
    getInvoice(invoiceId)
      .then((inv) => {
        setInvoice(inv);
        setLastChecked(new Date());
        if (inv.status === "paid" || inv.status === "completed" || inv.status === "late_paid") setIsPaid(true);
        if (inv.expiresAt) {
          const secs = Math.max(0, Math.floor((inv.expiresAt - Date.now()) / 1000));
          setExpiryLeft(secs);
        }
      })
      .catch(() => setError("Could not load this payment invoice."))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  // Open the underpayment notice exactly once per invoice. We wait for the
  // invoice doc to load so we can quote the real amount, and we suppress
  // the popup when the invoice is already settled or expired — at that
  // point the warning is irrelevant. localStorage keys are scoped per
  // invoiceId so the same buyer doesn't see it on every refresh.
  useEffect(() => {
    if (!invoiceId || !invoice || isPaid || isExpired) return;
    let seen = false;
    try { seen = localStorage.getItem(underpaymentStorageKey) === "1"; } catch {/* SSR / blocked storage */}
    if (seen) return;
    // Small delay so the modal feels intentional rather than mid-render.
    // Also lets the page's first paint complete first to avoid CLS.
    const t = setTimeout(() => setShowUnderpaymentNotice(true), 250);
    return () => clearTimeout(t);
  }, [invoiceId, invoice, isPaid, isExpired, underpaymentStorageKey]);

  // ESC dismisses the toast. We deliberately do NOT auto-focus the
  // toast's button: per toast accessibility guidance, notifications must
  // not steal focus from the user's primary task (paying the invoice).
  // Screen readers are still informed via `role="status"` + `aria-live`
  // on the toast container. The toast persists until the user closes it
  // manually (no auto-dismiss) — buyers should not lose the warning by
  // looking away.
  useEffect(() => {
    if (!showUnderpaymentNotice) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismissUnderpaymentNotice();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showUnderpaymentNotice, dismissUnderpaymentNotice]);

  // ─── Seller profile ───────────────────────────────────────────────────────

  useEffect(() => {
    const wallet = invoice?.sellerWallet || state?.sellerWallet;
    if (!wallet) return;
    getDoc(doc(db, "users", wallet.toLowerCase()))
      .then((snap) => {
        if (!snap.exists()) return;
        const d = snap.data();
        setSeller({ username: d.username || "Recipient", photoURL: d.photoURL || d.profileImage || null, themeColor: d.collectSettings?.themeColor || "#61CE70" });
      })
      .catch(() => {});
  }, [invoice?.sellerWallet, state?.sellerWallet]);

  // ─── Expiry countdown (MM:SS) ─────────────────────────────────────────────

  useEffect(() => {
    if (expiryLeft <= 0 || isPaid) return;
    expiryRef.current = setInterval(() => {
      setExpiryLeft((p) => { if (p <= 1) { clearInterval(expiryRef.current!); return 0; } return p - 1; });
    }, 1000);
    return () => clearInterval(expiryRef.current!);
  }, [expiryLeft > 0, isPaid]);

  // ─── Init payment methods ─────────────────────────────────────────────────

  // Create a Coinbase Headless Onramp order for the buyer's chosen method
  // (Apple Pay / Google Pay embedded button, or card via the hosted
  // checkout). Funds are sent to the invoice's dedicated wallet, so the
  // page's Firestore listener detects settlement automatically.
  const handleCoinbaseHeadlessSubmit = useCallback(
    async (data: {
      country: string;
      countryCode: string;
      email: string;
      phoneNumber: string;
      paymentMethod: string;
    }) => {
      const res = await fetch(
        `${import.meta.env.VITE_SERVERURL}/create-coinbase-headless-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}`,
          },
          body: JSON.stringify({
            amount: amount.toString(),
            walletAddress: dedicatedAddress,
            email: data.email || buyerEmail,
            phoneNumber: data.phoneNumber,
            country: data.country,
            paymentMethod: data.paymentMethod,
          }),
        },
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(
          errData.error || "Failed to create Coinbase Onramp order",
        );
      }

      const resp = await res.json();
      if (!resp.orderId) throw new Error("No order ID received");

      return {
        orderId: resp.orderId,
        paymentLink: resp.paymentLink || "",
        mode: (resp.mode as "embed" | "redirect") || "embed",
      };
    },
    [amount, dedicatedAddress, buyerEmail],
  );

  const initLightning = useCallback(async (force = false) => {
    if (!dedicatedAddress || !amount) return;
    // Auto-runs (from the invoice-polling-driven effect) must bail out
    // if we already have an order, are mid-flight, OR have surfaced an
    // error to the user. Without this guard, every Firestore poll would
    // re-fire /lightning/create → hammering FixedFloat once the first
    // call already failed. Manual "Check again" passes force=true.
    if (!force && (lightningOrder || lightningLoading || lightningError)) return;
    const cached = sessionStorage.getItem(`lightning_${invoiceId}`);
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as LightningOrder;
        setLightningOrder(parsed);
        return;
      } catch {}
    }
    setLightningError(null);
    setLightningLoading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_SERVERURL}/lightning/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}` },
        // Only the `lightning-btc` ("Add Money via Lightning") flow pins
        // the rail to FixedFloat — the page also surfaces a "Powered by
        // ff.io" chip in that case so we want the attribution and the
        // routing to agree. The `lightning-cashapp` ("CashApp Pay") flow
        // keeps the default FF-primary-with-SimpleSwap-fallback routing
        // and renders no FF attribution.
        body: JSON.stringify({
          amount,
          toAddress: dedicatedAddress,
          ...(method === "lightning-btc" ? { forceProvider: "ff" } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setLightningError({
          message: data.error || "Could not create Lightning invoice.",
          code: data.code,
          minAmount: data.minAmount,
          eta: data.eta,
        });
        return;
      }
      setLightningOrder(data);
      sessionStorage.setItem(`lightning_${invoiceId}`, JSON.stringify(data));
    } catch (e: any) {
      setLightningError({
        message: e.message || "Could not create Lightning invoice.",
      });
    } finally {
      setLightningLoading(false);
    }
  }, [dedicatedAddress, amount, lightningOrder, lightningLoading, lightningError, invoiceId, method]);

  const initAddressQR = useCallback(async () => {
    if (!dedicatedAddress || addressQR) return;
    try {
      const qr = await QRCode.toDataURL(dedicatedAddress, { width: 280, margin: 2, errorCorrectionLevel: "M", color: { dark: "#000000", light: "#FFFFFF" } });
      setAddressQR(qr);
    } catch {}
  }, [dedicatedAddress, addressQR]);

  // Check peer extension state on mount for wallet-apps method
  const initPeerExtension = useCallback(async () => {
    try {
      const s = await peerExtensionSdk.getState();
      setPeerState(s as any);
    } catch {
      setPeerState("needs_install");
    }
  }, []);

  useEffect(() => {
    if (!invoice || !dedicatedAddress) return;
    // Don't kick off any payment-method initialization once the invoice
    // has expired — no Coinbase session, no Lightning order, no QR, etc.
    if (isExpired) return;
    if (method === "coinbase-onramp") {
      // Auto-open the Coinbase modal once on landing (mirrors the previous
      // auto-launch). If the buyer closes it, the CTA card below reopens it.
      if (!coinbaseAutoOpened.current) {
        coinbaseAutoOpened.current = true;
        setCoinbaseModalOpen(true);
      }
    }
    // Both Lightning ids (lightning-btc + lightning-cashapp) trigger
    // the same Lightning create flow — the divergence between them is
    // the ff.io attribution chip and the forceProvider flag inside
    // initLightning, NOT the init trigger itself.
    else if (isLightning) initLightning();
    else if (method === "wallet") initAddressQR();
    else if (method === "cashapp") { initAddressQR(); initPeerExtension(); }
  }, [invoice, dedicatedAddress, method, isExpired]);

  // ─── Polling (secondary detection) ───────────────────────────────────────

  const checkStatus = useCallback(async () => {
    if (!invoiceId || isPaid) return;
    setIsPolling(true);
    try {
      const inv = await getInvoice(invoiceId);
      setInvoice(inv);
      setLastChecked(new Date());
      if (inv.status === "paid" || inv.status === "completed" || inv.status === "late_paid") {
        setIsPaid(true);
        clearInterval(pollRef.current!);
      }
    } catch {} finally { setIsPolling(false); }
  }, [invoiceId, isPaid]);

  useEffect(() => {
    if (isPaid) return;
    // Poll every 6 seconds as backup to Firestore listener
    pollRef.current = setInterval(checkStatus, 6000);
    return () => clearInterval(pollRef.current!);
  }, [checkStatus, isPaid]);

  // ─── Peer extension flow ──────────────────────────────────────────────────

  const triggerPeerExtension = async () => {
    if (peerState === "needs_install") {
      window.open("/extension-info", "_blank");
      return;
    }
    setShowPeerDisclaimer(true);
  };

  const proceedPeerExtension = async () => {
    setShowPeerDisclaimer(false);
    setPeerProcessing(true);
    try {
      let s = peerState as string;
      if (s === "needs_connection") {
        const approved = await peerExtensionSdk.requestConnection();
        if (!approved) { toast.error("Connection not approved"); return; }
        s = await peerExtensionSdk.getState() as string;
        setPeerState(s as any);
      }
      if (s !== "ready") { toast.error("Extension not ready. Please install or enable it."); return; }
      peerExtensionSdk.onramp({
        referrer: REFERRER, referrerLogo: REFERRER_LOGO, callbackUrl: CALLBACK_URL,
        inputCurrency: "USD", inputAmount: amount.toString(),
        toToken: BASE_USDC_TOKEN, recipient: dedicatedAddress,
      } as any);
    } catch (e: any) {
      toast.error(e.message || "Failed to open extension.");
    } finally { setPeerProcessing(false); }
  };

  // ─── Receipt download ─────────────────────────────────────────────────────

  const downloadReceipt = () => {
    const dateStr = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    const timeStr = new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    const incomingTx = invoice?.incomingTxHash || "";
    const sweepTx    = invoice?.sweepTxHash || "";

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>CashTap Receipt – ${invoice?.invoiceId || invoiceId}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#f9fafb;color:#1a1a1a;padding:40px}
.receipt{max-width:520px;margin:0 auto;background:#fff;border:1px solid #e5e7eb;border-radius:16px;padding:36px;box-shadow:0 4px 24px rgba(0,0,0,.06)}
.header{text-align:center;margin-bottom:24px}
.brand{font-size:20px;font-weight:800;color:#61CE70;letter-spacing:-.5px}
.title{font-size:22px;font-weight:700;margin:8px 0 4px}
.sub{color:#6b7280;font-size:13px}
.amount{text-align:center;font-size:48px;font-weight:800;color:#61CE70;margin:20px 0 4px}
.symbol{text-align:center;font-size:14px;color:#6b7280;margin-bottom:16px}
.badge{display:inline-block;background:#dcfce7;color:#15803d;font-size:12px;font-weight:700;padding:5px 16px;border-radius:999px;margin-bottom:24px}
.divider{border-top:1px solid #f3f4f6;margin:20px 0}
.section-title{font-size:11px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:.08em;margin-bottom:12px}
.row{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;gap:12px}
.label{color:#6b7280;font-size:12px;font-weight:600;white-space:nowrap}
.value{font-size:13px;font-weight:500;text-align:right;word-break:break-all;color:#111827}
.value.mono{font-family:monospace;font-size:11px;color:#374151}
.link{color:#3b82f6;text-decoration:none}
.footer{text-align:center;margin-top:28px;font-size:11px;color:#9ca3af;line-height:1.6}
</style></head>
<body><div class="receipt">
  <div class="header">
    <div class="brand">CashTap</div>
    <div class="title">Payment Receipt</div>
    <div class="sub">${dateStr} at ${timeStr}</div>
  </div>
  <div style="text-align:center">
    <div class="amount">$${amount.toFixed(2)}</div>
    <div class="symbol">USD</div>
    <span class="badge">✓ Payment Confirmed</span>
  </div>
  <div class="divider"></div>
  <div class="section-title">Payment Details</div>
  <div class="row"><span class="label">Invoice ID</span><span class="value">${invoice?.invoiceId || invoiceId}</span></div>
  <div class="row"><span class="label">Type</span><span class="value">${pageTitle}</span></div>
  <div class="row"><span class="label">Payment Method</span><span class="value">${METHOD_LABELS[method] || method}</span></div>
  <div class="divider"></div>
  <div class="section-title">Parties</div>
  <div class="row"><span class="label">Paid By (Buyer)</span><span class="value">${buyerEmail}</span></div>
  <div class="row"><span class="label">Recipient (Vendor)</span><span class="value">${sellerName}</span></div>
  <div class="divider"></div>
  <div class="section-title">Transaction IDs</div>
  ${incomingTx
    ? `<div class="row"><span class="label">Incoming Tx</span><span class="value mono"><a class="link" href="https://basescan.org/tx/${incomingTx}" target="_blank">${incomingTx.slice(0,12)}…${incomingTx.slice(-8)}</a></span></div>`
    : ""}
  ${sweepTx
    ? `<div class="row"><span class="label">Sweep Tx</span><span class="value mono"><a class="link" href="https://basescan.org/tx/${sweepTx}" target="_blank">${sweepTx.slice(0,12)}…${sweepTx.slice(-8)}</a></span></div>`
    : ""}
  ${!incomingTx && !sweepTx ? `<div class="row"><span class="label">Tx Hash</span><span class="value">Pending confirmation</span></div>` : ""}
  <div class="divider"></div>
  <div class="footer">
    Powered by <strong>CashTap</strong> · cashtap.cash<br/>
    This receipt is proof of payment. Keep it for your records.
  </div>
</div></body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `cashtap-receipt-${invoice?.invoiceId || invoiceId}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ─── Status steps ─────────────────────────────────────────────────────────

  const buildSteps = (): StatusStep[] => {
    const s = invoice?.status || "pending";
    const isDone = s === "paid" || s === "completed" || s === "late_paid";
    return [
      { label: "Invoice Created", desc: `Invoice ${(invoice?.invoiceId || invoiceId || "").slice(0, 16)}… issued`, state: "done" },
      { label: "Payment Initiated", desc: `${METHOD_LABELS[method] || "Payment"} selected`, state: isDone ? "done" : "active" },
      { label: "Awaiting Deposit", desc: "Monitoring blockchain for incoming funds", state: isDone ? "done" : "active" },
      { label: "Funds Confirmed", desc: "Auto-sweep to recipient wallet complete", state: isDone ? "done" : "pending" },
    ];
  };

  // ─── Expiry display ───────────────────────────────────────────────────────

  const expiryMins = String(Math.floor(expiryLeft / 60)).padStart(2, "0");
  const expirySecs = String(expiryLeft % 60).padStart(2, "0");
  const isUrgent = expiryLeft < 300 && expiryLeft > 0;
  // (isExpired is declared earlier so it can gate effects.)

  // ─── Loading / Error ──────────────────────────────────────────────────────

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#61CE70] animate-spin" />
        </div>
        <p className="text-gray-400 text-sm">Loading your invoice…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 max-w-sm w-full text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Invoice Not Found</h2>
        <p className="text-gray-400 text-sm mb-6">{error}</p>
        <Link to="/" className="inline-flex items-center gap-2 text-[#61CE70] font-medium text-sm"><ArrowLeft size={16} /> Back to CashTap</Link>
      </div>
    </div>
  );

  // ─── Main render ──────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16]">
      <Toaster position="top-center" />

      {/* Coinbase Headless Onramp modal — Apple Pay / Google Pay (embedded)
          and card (hosted checkout). Funds land on the invoice's dedicated
          wallet; the Firestore listener flips the page to "paid". */}
      <CoinbaseHeadlessOnrampModal
        isOpen={coinbaseModalOpen}
        onClose={() => setCoinbaseModalOpen(false)}
        onSubmit={handleCoinbaseHeadlessSubmit}
        onCheckStatus={(orderId) =>
          refreshCoinbaseOrderStatus(orderId).then((r) => r?.status ?? null)
        }
        amount={amount}
        theme="dark"
      />

      {/* Peer disclaimer modal */}
      {showPeerDisclaimer && (
        <PeerDisclaimerModal
          onClose={() => setShowPeerDisclaimer(false)}
          onProceed={proceedPeerExtension}
        />
      )}

      {/* Payment status modal — mobile-only entry point for the
          StatusTracker. Triggered by the "View Payment Status" button in
          the unified card. The same tracker still renders inline in the
          desktop left column, so this modal carries no state of its own. */}
      {showStatusModal && !isPaid && !isExpired && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Payment status"
          onClick={() => setShowStatusModal(false)}
        >
          <div
            className="w-full max-w-md max-h-[85vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <StatusTracker
              steps={buildSteps()}
              onRefresh={checkStatus}
              isPolling={isPolling}
              lastChecked={lastChecked}
            />
            <button
              type="button"
              onClick={() => setShowStatusModal(false)}
              className="mt-2 w-full py-2.5 rounded-2xl bg-gray-800/70 hover:bg-gray-700/70 text-gray-300 hover:text-white text-sm font-medium transition-colors cursor-pointer border border-gray-700/50"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Underpayment notice — persistent bottom-right toast.
          Intentionally compact: width-capped at ~260–280px, tight
          padding, single inline icon, and short copy that fits in two
          lines on a phone. The previous version ate ~25% of mobile
          viewport, which hid the QR / address card behind it. Still
          accessible (role=status + aria-live), still dismissable, and
          retains the amber accent stripe so it reads as a real
          warning, not a passive toast. */}
      {showUnderpaymentNotice && (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          aria-labelledby="underpay-title"
          aria-describedby="underpay-body"
          className="fixed z-40 bottom-3 right-3 w-[260px] sm:w-[280px] max-w-[calc(100vw-1.5rem)] pointer-events-auto animate-[toastInRight_200ms_cubic-bezier(0.16,1,0.3,1)]"
        >
          <div className="relative bg-[#1A1308]/95 backdrop-blur border border-amber-400/40 rounded-xl shadow-[0_6px_20px_-6px_rgba(251,191,36,0.35)] overflow-hidden">
            {/* Slim accent stripe — same intent as before, half the width */}
            <div className="absolute top-0 left-0 bottom-0 w-0.5 bg-amber-400" />

            <div className="flex items-start gap-2 p-2.5 pl-3">
              <AlertCircle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />

              <div className="flex-1 min-w-0">
                <p
                  id="underpay-title"
                  className="text-[12.5px] font-semibold text-white leading-tight"
                >
                  Pay exactly{" "}
                  <span className="text-amber-300 tabular-nums">
                    ${amount.toFixed(2)}
                  </span>
                </p>
                <p
                  id="underpay-body"
                  className="text-[11px] text-amber-100/70 leading-snug mt-0.5"
                >
                  Less goes on hold for manual review.
                </p>
              </div>

              <button
                type="button"
                onClick={dismissUnderpaymentNotice}
                aria-label="Dismiss notice"
                className="shrink-0 -mt-0.5 -mr-0.5 w-6 h-6 rounded-md flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/10 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Success overlay ── */}
      {isPaid && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-[#61CE70]/30 rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl">
            <div className="relative mx-auto mb-6 w-24 h-24">
              <div className="absolute inset-0 rounded-full bg-[#61CE70]/20 animate-ping" />
              <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-[#61CE70]/20 to-[#61CE70]/10 border border-[#61CE70]/40 flex items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-[#61CE70]" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Payment Received!
            </h2>
            <p className="text-gray-400 text-sm mb-2">
              Your {pageTitle.toLowerCase()} of{" "}
              <span className="text-white font-semibold">
                ${amount.toFixed(2)}
              </span>{" "}
              was confirmed.
            </p>
            <p className="text-gray-500 text-xs mb-6">
              Receipt sent to{" "}
              <span className="text-gray-300">{buyerEmail}</span>
            </p>
            <div className="bg-[#0A0D12] rounded-2xl p-4 mb-6 space-y-2 text-left border border-gray-800/50">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Amount</span>
                <span className="text-white font-semibold">
                  ${amount.toFixed(2)} USD
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Recipient</span>
                <span className="text-white">{sellerName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Invoice</span>
                <span className="text-gray-300 text-xs font-mono">
                  {(invoiceId || "").slice(0, 16)}…
                </span>
              </div>
            </div>
            <button
              onClick={downloadReceipt}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-[#61CE70] hover:bg-[#72D981] text-black font-bold transition-all cursor-pointer mb-3"
            >
              <Download className="w-4 h-4" /> Download Receipt
            </button>
            <Link
              to="/"
              className="block text-center text-gray-400 hover:text-white text-sm"
            >
              Back to CashTap
            </Link>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <header className="w-full border-b border-gray-800/50 bg-[#090D14]/80 backdrop-blur-lg sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 sm:py-4 lg:py-2 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-400 hover:text-white text-sm"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Back</span>
          </Link>
          <Link to="/">
            <img
              src="/logo.png"
              alt="CashTap"
              className="h-7 sm:h-8 lg:h-7 w-auto"
            />
          </Link>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Shield className="w-3.5 h-3.5 text-[#61CE70]" />
            <span className="hidden sm:inline">Secured</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-2 sm:py-10 lg:py-4">
        {/* ── Seller banner (desktop only — mobile uses the in-card header) ── */}
        <div className="hidden lg:flex bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-3 items-center gap-3 mb-3">
          <div className="relative flex-shrink-0">
            {sellerPhoto ? (
              <img
                src={sellerPhoto}
                alt={sellerName}
                className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl object-cover border-2 border-gray-700/50"
              />
            ) : (
              <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-[#61CE70]/20 to-[#61CE70]/10 border border-[#61CE70]/30 flex items-center justify-center">
                <span className="text-xl sm:text-2xl font-bold text-[#61CE70]">
                  {sellerName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[#61CE70] border-2 border-[#0F1419] flex items-center justify-center">
              <Check
                className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-black"
                strokeWidth={3}
              />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-[#61CE70]/10 border border-[#61CE70]/20 text-[#61CE70] font-medium mb-1">
              {SOURCE_ICONS[source]} {pageTitle}
            </span>
            <h1 className="text-lg font-bold text-white truncate">
              {source === "buymeCoffee"
                ? `Buy ${sellerName} a Coffee`
                : source === "donation"
                  ? `Support ${sellerName}`
                  : sellerName}
            </h1>
            <p className="text-gray-500 text-xs">via CashTap</p>
          </div>

          {/* Desktop expiry — compact single-line pill so the banner stays
              short enough for the whole page to fit in 100vh on a laptop. */}
          {!isPaid && expiryLeft > 0 && (
            <div
              role="timer"
              aria-live="polite"
              className={`hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold shrink-0 ${
                isUrgent
                  ? "border-red-500/40 bg-red-500/10 text-red-300"
                  : "border-amber-500/35 bg-amber-500/10 text-amber-300"
              }`}
            >
              <Clock className="w-3.5 h-3.5 shrink-0" />
              <span className="uppercase tracking-wider text-[10px] opacity-80">
                Expires in
              </span>
              <span className="tabular-nums font-bold">
                {expiryMins}:{expirySecs}
              </span>
            </div>
          )}
        </div>

        {/* ── 2-col grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-6 lg:gap-4">
          {/* LEFT (mobile) / RIGHT (desktop): invoice summary + payment action.
              On mobile/tablet (< lg) this whole column collapses into ONE
              bordered card — seller, amount, timer, QR/CTA and invoice meta
              all live inside it, separated by dividers. On desktop (lg+) the
              wrapper goes chrome-less and the original stacked cards return,
              and `lg:order-2` moves the action surface to the visual right
              column so the payment status sits on the left. */}
          <div className="space-y-0 border border-gray-700/60 rounded-2xl overflow-hidden bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] lg:order-2 lg:self-start lg:border-0 lg:rounded-none lg:overflow-visible lg:bg-transparent lg:bg-none">
            {/* Mobile: in-card seller header */}
            <div className="lg:hidden flex flex-col items-center text-center px-3 pt-3 pb-2.5 border-b border-gray-800/60">
              <div className="relative mb-1.5">
                {sellerPhoto ? (
                  <img
                    src={sellerPhoto}
                    alt={sellerName}
                    className="w-11 h-11 rounded-full object-cover border-2 border-[#61CE70]/40"
                  />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#61CE70]/20 to-[#61CE70]/10 border-2 border-[#61CE70]/40 flex items-center justify-center">
                    <span className="text-lg font-bold text-[#61CE70]">
                      {sellerName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#61CE70] border-2 border-[#0F1419] flex items-center justify-center">
                  <Check className="w-2 h-2 text-black" strokeWidth={3} />
                </div>
              </div>
              <h1 className="text-base font-bold text-white truncate max-w-full leading-tight">
                {source === "buymeCoffee"
                  ? `Buy ${sellerName} a Coffee`
                  : source === "donation"
                    ? `Support ${sellerName}`
                    : sellerName}
              </h1>
              <p className="text-gray-500 text-[10px]">via CashTap</p>
            </div>

            {/* Invoice summary — mobile only. On desktop a sibling copy
                lives in the visual-left column (see RIGHT div below) so
                the amount + invoice meta sit alongside the Payment Status
                tracker rather than stacking under the QR. */}
            <div className="overflow-hidden lg:hidden">
              <div className="px-4 pt-2.5 pb-2.5 sm:px-6 sm:pt-6 sm:pb-5 lg:border-b lg:border-gray-700/50 text-center">
                <p className="text-[9px] sm:text-[10px] text-gray-600 uppercase tracking-widest font-semibold mb-0.5 sm:mb-2">
                  Amount Due
                </p>
                <p className="text-4xl sm:text-6xl font-black text-white tracking-tight leading-none">
                  ${amount.toFixed(2)}
                  <span className="text-sm sm:text-lg text-gray-500 font-normal ml-1">
                    USD
                  </span>
                </p>
                {/* Desktop: status badge */}
                <div className="hidden lg:inline-flex mt-3">
                  {isPaid ? (
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#61CE70]/15 border border-[#61CE70]/30 text-[#61CE70] text-xs font-semibold">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Paid
                    </span>
                  ) : isExpired ? (
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-xs font-semibold">
                      <AlertCircle className="w-3.5 h-3.5" /> Expired
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs font-semibold">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />{" "}
                      Awaiting Payment
                    </span>
                  )}
                </div>
                {/* Mobile: live countdown pill (active) or status badge (paid/expired) */}
                <div className="lg:hidden inline-flex mt-1.5">
                  {isPaid ? (
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#61CE70]/15 border border-[#61CE70]/30 text-[#61CE70] text-[11px] font-semibold">
                      <CheckCircle2 className="w-3 h-3" /> Paid
                    </span>
                  ) : isExpired ? (
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-[11px] font-semibold">
                      <AlertCircle className="w-3 h-3" /> Expired
                    </span>
                  ) : (
                    <span
                      role="timer"
                      aria-live="polite"
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${
                        isUrgent
                          ? "border-red-500/40 bg-red-500/10 text-red-300"
                          : "border-amber-500/35 bg-amber-500/10 text-amber-300"
                      }`}
                    >
                      <Clock className="w-3 h-3 shrink-0" />
                      <span className="uppercase tracking-wider text-[9px] opacity-80">
                        Time Remaining
                      </span>
                      <span className="tabular-nums font-bold">
                        {expiryMins}:{expirySecs}
                      </span>
                    </span>
                  )}
                </div>
              </div>
              <div className="hidden lg:block px-4 py-2.5 space-y-2 sm:px-6 sm:py-5 sm:space-y-3.5">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-xs sm:text-sm">
                    Invoice ID
                  </span>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <span className="text-gray-300 text-xs font-mono">
                      {(invoice?.invoiceId || invoiceId || "").slice(0, 16)}…
                    </span>
                    <CopyButton
                      text={invoice?.invoiceId || invoiceId || ""}
                      label="Copy"
                    />
                  </div>
                </div>
                {buyerEmail && (
                  <div className="hidden sm:flex items-center justify-between">
                    <span className="text-gray-500 text-sm">Your Email</span>
                    <span className="text-white text-sm font-medium">
                      {buyerEmail}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-xs sm:text-sm">
                    Payment Method
                  </span>
                  <span className="flex items-center gap-1.5 text-white text-xs sm:text-sm font-medium">
                    {METHOD_ICON[method]}
                    {METHOD_LABELS[method] || method}
                  </span>
                </div>
                {/* Only surface the "Network: Base (USDC)" row for the
                    external-wallet flow, where the buyer is manually
                    sending from their own wallet and genuinely needs to
                    know the chain + token. For all hosted rails (Lightning
                    / CashApp / Coinbase / Stripe) the buyer pays in USD
                    and the chain detail is internal plumbing — showing it
                    makes the flow feel crypto-y for no benefit. */}
                {method === "wallet" && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-sm">Network</span>
                    <span className="flex items-center gap-1.5 text-white text-sm">
                      <span className="w-2 h-2 rounded-full bg-blue-400" />
                      Base (USDC)
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Expired notice — replaces every payment action when the
                invoice has timed out. Buttons, timers, and auto-launches
                are all gated above so this card is the only thing the
                buyer sees in the action slot once isExpired is true. */}
            {!isPaid && isExpired && (
              <div className="overflow-hidden lg:bg-gradient-to-br lg:from-red-950/40 lg:to-[#1A1F2B] lg:border lg:border-red-500/30 lg:rounded-3xl">
                <div className="px-6 py-4 border-b border-red-500/20 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  <h3 className="text-sm font-semibold text-white">
                    Invoice Expired
                  </h3>
                </div>
                <div className="p-6 flex flex-col items-center text-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-red-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold mb-1">
                      This invoice is no longer accepting payments
                    </p>
                    <p className="text-gray-400 text-sm">
                      Don't send funds — the dedicated address h as been retired
                      and any deposit will not be credited. Please request a new
                      invoice from the recipient.
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled
                    className="w-full py-3 rounded-2xl bg-gray-800/60 border border-gray-700/40 text-gray-500 font-semibold text-sm cursor-not-allowed"
                  >
                    Payment Closed
                  </button>
                  <Link
                    to="/"
                    className="text-xs text-gray-500 hover:text-gray-300 underline"
                  >
                    Back to CashTap
                  </Link>
                </div>
              </div>
            )}

            {/* Payment action card */}
            {!isPaid && !isExpired && (
              <div className="overflow-hidden lg:bg-gradient-to-br lg:from-[#0F1419] lg:to-[#1A1F2B] lg:border lg:border-gray-700/50 lg:rounded-2xl">
                {/* Header — desktop only; on mobile the amount + QR speak for themselves */}
                <div className="hidden lg:flex px-4 py-3 sm:px-6 sm:py-4 lg:px-4 lg:py-2 border-b border-gray-700/40 items-center gap-2">
                  {METHOD_ICON[method]}
                  <h3 className="text-sm lg:text-xs font-semibold text-white">
                    Complete Your Payment
                  </h3>
                </div>
                <div className="p-3 min-[400px]:p-4 sm:p-6 lg:p-3">
                  {/* ─── COINBASE (Headless Onramp) ─── */}
                  {method === "coinbase-onramp" && (
                    <div className="flex flex-col items-center gap-5 py-2">
                      {/* Logo + accepted methods */}
                      <div className="w-20 h-20 rounded-3xl bg-[#0052FF]/10 border-2 border-[#0052FF]/30 flex items-center justify-center">
                        <img
                          src="/coinbase-new.png"
                          alt="Coinbase"
                          className="w-12 h-12 p-1 rounded-lg bg-white object-contain"
                        />
                      </div>
                      <div className="flex items-center gap-2 flex-wrap justify-center">
                        {[
                          { src: "/Visa-logo.png", alt: "Visa", h: "h-4" },
                          { src: "/mastercard-logo.png", alt: "Mastercard", h: "h-5" },
                          { src: "/applepay-logo.png", alt: "Apple Pay", h: "h-5" },
                          { src: "/googlepay-logo.png", alt: "Google Pay", h: "h-5" },
                        ].map((logo) => (
                          <div
                            key={logo.alt}
                            className="flex items-center justify-center rounded-lg px-2 py-1.5 bg-white shadow-sm"
                          >
                            <img
                              src={logo.src}
                              alt={logo.alt}
                              className={`${logo.h} w-auto object-contain`}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="text-center max-w-xs">
                        <p className="text-white text-base font-semibold tracking-tight mb-1">
                          Pay with Visa / MasterCard, Apple Pay or Google Pay
                        </p>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          Complete your{" "}
                          <span className="text-white font-medium">
                            ${amount.toFixed(2)}
                          </span>{" "}
                          payment securely. This page updates automatically once
                          it's received.
                        </p>
                      </div>
                      <button
                        onClick={() => setCoinbaseModalOpen(true)}
                        className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-2xl font-bold text-sm text-white transition-all cursor-pointer active:scale-[0.98]"
                        style={{
                          background:
                            "linear-gradient(135deg, #0052FF 0%, #1a6aff 100%)",
                        }}
                      >
                        <CreditCard className="w-4 h-4" />
                        Pay ${amount.toFixed(2)}
                      </button>
                      <p className="text-center text-xs text-gray-600">
                        Funds will be sent to your dedicated payment address and
                        tracked automatically.
                      </p>
                    </div>
                  )}

                  {/* ─── CASHAPP PAY / LIGHTNING ─── */}
                  {/* Same render branch handles both Lightning ids.
                      `lightning-btc` adds the ff.io chip inside; the
                      mobile chip below the trust badges (line ~2080) is
                      ALSO gated on `lightning-btc` so the "CashApp Pay"
                      variant stays free of FF attribution. */}
                  {isLightning && (
                    <div className="space-y-3 sm:space-y-4 lg:space-y-2">
                      {lightningLoading ? (
                        <div className="flex flex-col items-center gap-3 py-6 sm:py-8">
                          <Loader2 className="w-8 h-8 text-[#00D632] animate-spin" />
                          <p className="text-gray-400 text-sm">
                            Creating Lightning invoice…
                          </p>
                        </div>
                      ) : lightningError ? (
                        <div className="py-1">
                          <div className="mx-auto w-14 h-14 lg:w-12 lg:h-12 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
                            <Clock className="w-7 h-7 lg:w-6 lg:h-6 text-amber-400" />
                          </div>
                          <h3 className="mt-3 lg:mt-2 text-center text-white font-bold text-base lg:text-sm">
                            Temporarily Unavailable
                          </h3>
                          <p className="mt-1 text-center text-gray-400 text-xs sm:text-[13px] lg:text-xs leading-relaxed px-2">
                            {lightningError.minAmount ? (
                              <>
                                CashApp Pay isn't available for amounts under{" "}
                                <span className="text-gray-200 font-semibold">
                                  ${lightningError.minAmount}
                                </span>{" "}
                                right now.
                              </>
                            ) : (
                              "CashApp Pay via Lightning is temporarily unavailable."
                            )}
                          </p>
                          <div className="mt-3 lg:mt-2 flex items-center gap-2 rounded-xl bg-[#0A0D12] border border-gray-800 px-3 py-2.5 lg:py-2">
                            <Clock className="w-4 h-4 text-amber-400 shrink-0" />
                            <span className="text-gray-500 text-xs">
                              Expected back
                            </span>
                            <span className="ml-auto text-amber-300 text-xs font-semibold">
                              {lightningError.eta || "soon"}
                            </span>
                          </div>
                          <div className="mt-2 flex items-start gap-2 rounded-xl bg-[#00D632]/5 border border-[#00D632]/20 px-3 py-2.5 lg:py-2">
                            <Zap className="w-4 h-4 text-[#00D632] shrink-0 mt-0.5" />
                            <p className="text-gray-300 text-xs leading-relaxed">
                              {lightningError.minAmount ? (
                                <>
                                  Pay{" "}
                                  <span className="text-white font-semibold">
                                    ${lightningError.minAmount} or more
                                  </span>{" "}
                                  to continue now — or choose a different
                                  payment method.
                                </>
                              ) : (
                                "Please try again shortly or choose a different payment method."
                              )}
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              if (invoice?.collectSlug) {
                                navigate(`/cashapppay/${invoice.collectSlug}`);
                              } else {
                                navigate(-1);
                              }
                            }}
                            className="mt-3 lg:mt-2 w-full flex items-center justify-center gap-2 px-5 py-2.5 lg:py-2 rounded-xl bg-[#00D632] hover:bg-[#00B82A] text-white text-sm font-semibold transition-colors cursor-pointer shadow-sm shadow-[#00D632]/30"
                          >
                            <ArrowLeft className="w-4 h-4" />
                            Change amount
                          </button>
                          <button
                            onClick={initLightning as any}
                            className="mt-2 w-full flex items-center justify-center gap-2 px-5 py-2 lg:py-1.5 rounded-xl bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-medium transition-colors cursor-pointer"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Check again
                          </button>
                        </div>
                      ) : lightningOrder ? (
                        <>
                          {/* Branded header — compact on desktop to keep the page in 100vh. */}

                          {/* Order details — hidden on mobile to save height. */}
                          <div className="hidden sm:block bg-[#0A0D12] rounded-2xl p-4 lg:p-3 space-y-2.5 lg:space-y-1.5 text-sm lg:text-[13px]">
                            <div className="flex justify-between">
                              <span className="text-gray-500">
                                CashTap Invoice
                              </span>
                              <span className="text-gray-300 font-mono text-xs">
                                {(invoice?.invoiceId || invoiceId || "").slice(
                                  0,
                                  14,
                                )}
                                …
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">
                                Order Number
                              </span>
                              <div className="flex items-center gap-1.5">
                                <span className="text-gray-300 font-mono text-xs">
                                  {lightningOrder.orderId}
                                </span>
                                <CopyButton
                                  text={lightningOrder.orderId}
                                  label=""
                                />
                              </div>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">You Pay</span>
                              <span className="text-white font-semibold">
                                ${amount.toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">
                                Recipient Gets
                              </span>
                              <span className="text-[#61CE70] font-semibold">
                                ${amount.toFixed(2)}
                              </span>
                            </div>
                          </div>

                          {/* QR — hidden until checkbox confirmed for lightning-btc only; always shown for other methods */}
                          {lightningOrder?.invoice && (
                            <div className="flex flex-col items-center gap-1.5 sm:gap-2 lg:gap-1">
                              <p className="text-gray-500 md:flex hidden text-xs lg:text-[11px] md:mt-0 -mt-3">
                                {method === "lightning-btc"
                                  ? "Scan with any Lightning wallet"
                                  : "Scan with CashApp on another device"}
                              </p>
                              <div className="p-2.5 sm:p-3 lg:p-2 md:mt-0 -mt-5 bg-white rounded-2xl lg:rounded-xl shadow-lg">
                                {method === "lightning-btc" && !agreedToTerms ? (
                                  <div className="w-36 h-36 min-[400px]:w-40 min-[400px]:h-40 sm:w-52 sm:h-52 lg:w-40 lg:h-40 flex flex-col items-center justify-center gap-2 bg-gray-100 rounded-xl">
                                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                                    </svg>
                                    <p className="text-[11px] text-gray-400 text-center px-4 leading-snug">
                                      Confirm above to reveal QR code
                                    </p>
                                  </div>
                                ) : (
                                  <StyledQR
                                    value={`LIGHTNING:${lightningOrder.invoice.toUpperCase()}`}
                                    className="w-36 h-36 min-[400px]:w-40 min-[400px]:h-40 sm:w-52 sm:h-52 lg:w-40 lg:h-40"
                                    logo={method === "lightning-btc" ? "none" : "cashapp"}
                                  />
                                )}
                              </div>
                            </div>
                          )}

                          {/* CashApp Pay button */}
                          {/* Mandatory consent — buyer must acknowledge
                              that the transfer is irreversible before
                              the launch CTA becomes interactive. Same
                              wording, behaviour and visual treatment as
                              the gate on the standalone LightningPaymentPage. */}
                          <label className="flex items-start gap-2.5 cursor-pointer select-none px-1">
                            <input
                              type="checkbox"
                              checked={agreedToTerms}
                              onChange={(e) => setAgreedToTerms(e.target.checked)}
                              aria-describedby="invoice-cashapp-irreversible-disclaimer"
                              className="mt-0.5 w-4 h-4 rounded border border-gray-600 bg-gray-900/60 text-[#61CE70] focus:ring-1 focus:ring-[#61CE70]/60 focus:ring-offset-0 accent-[#61CE70] shrink-0 cursor-pointer"
                            />
                            <span
                              id="invoice-cashapp-irreversible-disclaimer"
                              className="text-[11px] sm:text-xs text-gray-400 leading-relaxed"
                            >
                              By continuing, you confirm this transfer is
                              authorized by you and is final.{" "}
                              <strong className="text-white">
                                Transfers are irreversible.
                              </strong>{" "}
                              CashTap does not hold, cancel, or reverse
                              completed transfers. Beware of scams.
                            </span>
                          </label>

                          {/* Two CTA variants share the same disclaimer
                              gate; the differences track the Lightning
                              id semantics established above:
                                • lightning-cashapp → CashApp-branded
                                  button + cash.app launch URL.
                                • lightning-btc     → Lightning-branded
                                  button + generic `lightning:` URI so
                                  ANY installed Lightning wallet can
                                  handle the tap. The button keeps the
                                  CashTap green to stay visually
                                  consistent with the rest of the page.
                              The href, icon and label diverge; the
                              consent gate, dimming, disabled handling
                              and a11y attributes are identical. */}
                          {(() => {
                            const isCashappVariant = method === "lightning-cashapp";
                            if (!isCashappVariant) return null;
                            const href = `https://cash.app/launch/lightning/${lightningOrder.invoice}`;
                            return (
                              <a
                                href={agreedToTerms ? href : undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                  if (!agreedToTerms) {
                                    e.preventDefault();
                                    toast.error(
                                      "Please confirm the disclaimer to continue",
                                    );
                                  }
                                }}
                                aria-disabled={!agreedToTerms}
                                className={`flex items-center justify-center gap-2 sm:gap-2.5 w-full py-3 sm:py-3.5 lg:py-2.5 rounded-2xl lg:rounded-xl font-bold text-sm lg:text-[13px] text-white transition-all ${
                                  agreedToTerms
                                    ? "cursor-pointer hover:opacity-90 active:scale-[0.98]"
                                    : "cursor-not-allowed"
                                }`}
                                style={{
                                  background:
                                    "linear-gradient(135deg, #00D632 0%, #00b32a 100%)",
                                  opacity: agreedToTerms ? 1 : 0.45,
                                  boxShadow: agreedToTerms
                                    ? "0 8px 32px rgba(0,214,50,0.22)"
                                    : "none",
                                }}
                              >
                                <SiCashapp className="w-4 h-4 sm:w-5 sm:h-5 lg:w-4 lg:h-4" />
                                Pay with CashApp Pay
                                <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-3.5 lg:h-3.5" />
                              </a>
                            );
                          })()}


                          {/* BOLT11 — full display on tablet + desktop (hidden until checkbox confirmed for lightning-btc only) */}
                          {(method !== "lightning-btc" || agreedToTerms) && (
                            <div className="hidden sm:block bg-[#0A0D12] rounded-2xl lg:rounded-xl p-4 lg:p-2.5">
                              <div className="flex items-center justify-between mb-2 lg:mb-1">
                                <span className="text-xs lg:text-[10px] text-gray-500 uppercase font-semibold tracking-wide">
                                  Payment Code
                                </span>
                                <CopyButton
                                  text={lightningOrder.invoice}
                                  label="Copy"
                                />
                              </div>
                              <p className="text-xs lg:text-[10px] text-gray-600 font-mono break-all leading-relaxed">
                                {lightningOrder.invoice.slice(0, 80)}…
                              </p>
                            </div>
                          )}

                          {/* BOLT11 — compact copy button on mobile (hidden until checkbox confirmed for lightning-btc only) */}
                          {(method !== "lightning-btc" || agreedToTerms) && <CopyInvoiceButton invoice={lightningOrder.invoice} />}

                          {/* Lightning Invoice attribution.
                              Deliberately neutral — no CashApp logo, no
                              CashApp wording, no green-branded card —
                              because on the payment-invoice flow this
                              method is "Lightning Invoice" (provider:
                              FixedFloat) and is configured separately
                              from the `cashapp` method id (Wallet Apps
                              via the peer extension). Buyers tap the
                              launch CTA above to actually open Cash App
                              if they want; that's the destination, not
                              the representation of this method.
                              Only the "Powered by ff.io" chip remains
                              — same chip the Add Funds Lightning page
                              uses for cross-surface consistency. */}

                          {/* Trust badges — desktop only here (mobile moves them to the
                              bottom of the unified card, below the meta box). */}
                          <div className="hidden lg:flex items-center justify-between gap-1.5">
                            <div className="flex items-center gap-1 flex-1 justify-center">
                              <Shield className="w-3 h-3 text-[#61CE70] shrink-0" />
                              <span className="text-[9px] min-[400px]:text-[10px] text-gray-400 whitespace-nowrap">
                                Secure Payment
                              </span>
                            </div>
                            <div className="w-px h-3 bg-gray-800" />
                            <div className="flex items-center gap-1 flex-1 justify-center">
                              <Zap className="w-3 h-3 text-amber-400 shrink-0" />
                              <span className="text-[9px] min-[400px]:text-[10px] text-gray-400 whitespace-nowrap">
                                Instant Transfer
                              </span>
                            </div>
                            <div className="w-px h-3 bg-gray-800" />
                            <div className="flex items-center gap-1 flex-1 justify-center">
                              <Shield className="w-3 h-3 text-blue-400 shrink-0" />
                              <span className="text-[9px] min-[400px]:text-[10px] text-gray-400 whitespace-nowrap">
                                Encrypted & Safe
                              </span>
                            </div>
                          </div>
                          {/* Desktop / tablet attribution — mobile gets
                              a sibling chip rendered below the trust
                              badges instead, so on small screens the
                              attribution sits at the very bottom of the
                              unified card and doesn't push the CTA off
                              the fold. */}
                          {/* ff.io chip — only renders on the
                              `lightning-btc` ("Add Money via Lightning")
                              flow. `lightning-cashapp` ("CashApp Pay")
                              deliberately omits the attribution because
                              that flow is branded as CashApp Pay, not
                              as a Lightning-via-ff.io payment. */}
                          {method === "lightning-btc" && (
                            <div className="hidden md:flex items-center justify-center gap-2">
                              <a
                                href="https://ff.io"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Visit ff.io"
                                className="inline-flex bg-white rounded-lg px-2 py-1 items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer transition-transform duration-[5000ms] ease-out hover:scale-110"
                              >
                                <span>Powered by</span>
                                <img
                                  src="/ff-logo.png"
                                  alt="ff.io"
                                  className="h-8 rounded-lg ml-2 w-auto object-contain scale-125"
                                />
                              </a>
                            </div>
                          )}
                        </>
                      ) : null}
                    </div>
                  )}

                  {/* ─── WALLET APPS (peer extension) ─── */}
                  {method === "cashapp" && (
                    <div className="space-y-4">
                      {/* Primary: extension button */}
                      <div className="bg-gradient-to-br from-[#0A0D12] to-[#0F1419] border border-gray-700/40 rounded-2xl p-5">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-xl bg-[#61CE70]/10 border border-[#61CE70]/20 flex items-center justify-center">
                            <Smartphone className="w-5 h-5 text-[#61CE70]" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white">
                              Pay via CashApp or Venmo
                            </p>
                            <p className="text-xs text-gray-500">
                              Requires the CashTap browser extension
                            </p>
                          </div>
                        </div>

                        {peerState === "needs_install" ? (
                          <div className="space-y-3">
                            <p className="text-sm text-gray-400">
                              The CashTap extension is required to pay directly
                              from CashApp or Venmo.
                            </p>
                            <a
                              href="/extension-info"
                              target="_blank"
                              className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl bg-[#61CE70] hover:bg-[#72D981] text-black font-bold text-sm transition-all cursor-pointer"
                            >
                              <Zap className="w-4 h-4" /> Install CashTap
                              Extension
                            </a>
                          </div>
                        ) : (
                          <button
                            onClick={triggerPeerExtension}
                            disabled={peerProcessing}
                            className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl bg-[#61CE70] hover:bg-[#72D981] disabled:opacity-60 text-black font-bold text-sm transition-all cursor-pointer"
                          >
                            {peerProcessing ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <SiCashapp className="w-4 h-4" />
                            )}
                            {peerProcessing
                              ? "Opening extension…"
                              : "Pay with CashApp / Venmo"}
                          </button>
                        )}
                      </div>

                      {/* Divider */}
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-gray-700/50" />
                        <span className="text-xs text-gray-600 font-medium">
                          or send manually
                        </span>
                        <div className="flex-1 h-px bg-gray-700/50" />
                      </div>

                      {/* Fallback: manual QR */}
                      <div className="space-y-3">
                        <p className="text-xs text-gray-500 text-center">
                          Send{" "}
                          <strong className="text-white">
                            ${amount.toFixed(2)} USDC
                          </strong>{" "}
                          on <strong className="text-white">Base</strong> to
                          this address from any compatible wallet.
                        </p>
                        {addressQR ? (
                          <div className="flex justify-center">
                            <div className="p-3 bg-white rounded-2xl shadow-lg">
                              <img
                                src={addressQR}
                                alt="QR"
                                className="w-44 h-44"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="w-44 h-44 mx-auto bg-gray-800/50 rounded-2xl animate-pulse" />
                        )}
                        <div className="bg-[#0A0D12] rounded-2xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-gray-500 uppercase font-semibold tracking-wide">
                              Address
                            </span>
                            <CopyButton text={dedicatedAddress} label="Copy" />
                          </div>
                          <p className="text-xs text-gray-400 font-mono break-all">
                            {dedicatedAddress}
                          </p>
                        </div>
                        <p className="text-xs text-amber-500/70 flex items-center gap-1.5 justify-center">
                          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                          Only USDC on Base — other tokens won't be credited.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* ─── WALLET TRANSFER ─── */}
                  {method === "wallet" && (
                    <div className="space-y-4">
                      <div className="bg-[#61CE70]/5 border border-[#61CE70]/20 rounded-2xl p-4">
                        <p className="text-gray-400 text-sm">
                          Send exactly{" "}
                          <strong className="text-white">
                            ${amount.toFixed(2)} USDC
                          </strong>{" "}
                          on <strong className="text-white">Base</strong> to the
                          address below.
                        </p>
                      </div>
                      <div className="flex flex-col items-center gap-3">
                        {addressQR ? (
                          <div className="p-4 bg-white rounded-3xl shadow-xl">
                            <img
                              src={addressQR}
                              alt="QR"
                              className="w-52 h-52 sm:w-56 sm:h-56"
                            />
                          </div>
                        ) : (
                          <div className="w-52 h-52 bg-gray-800/50 rounded-3xl animate-pulse" />
                        )}
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#61CE70]/10 border border-[#61CE70]/30">
                          <span className="text-[#61CE70] font-bold">
                            ${amount.toFixed(2)} USDC
                          </span>
                          <span className="text-gray-500 text-sm">on Base</span>
                        </div>
                      </div>
                      <div className="bg-[#0A0D12] rounded-2xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500 uppercase font-semibold tracking-wide">
                            Recipient Address
                          </span>
                          <CopyButton text={dedicatedAddress} label="Copy" />
                        </div>
                        <p className="text-sm text-gray-300 font-mono break-all leading-relaxed">
                          {dedicatedAddress}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-xs text-gray-500">
                        <div className="flex items-center gap-1.5 bg-gray-800/30 rounded-xl p-2.5">
                          <Shield className="w-3.5 h-3.5 text-[#61CE70]" />
                          Dedicated address
                        </div>
                        <div className="flex items-center gap-1.5 bg-gray-800/30 rounded-xl p-2.5">
                          <Zap className="w-3.5 h-3.5 text-[#61CE70]" />
                          Auto-confirmed
                        </div>
                      </div>
                    </div>
                  )}

                  {/* CARD / BANK */}
                  {method === "card-bank" && (
                    <div className="flex flex-col items-center gap-4 py-6 text-center">
                      <CreditCard className="w-12 h-12 text-gray-500" />
                      <p className="text-gray-400 text-sm">
                        Your payment session was opened in the previous step.
                      </p>
                      <Link
                        to={-1 as any}
                        className="px-6 py-2.5 rounded-xl bg-gray-700 hover:bg-gray-600 text-white font-semibold text-sm cursor-pointer"
                      >
                        Go Back
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Mobile: invoice meta ──────────────────────────────
                Invoice ID + Payment Method, merged into one card. On
                desktop (lg+) these live in the summary card above; on
                mobile they move below the payment action so the amount,
                QR and CTA stay above the fold on small phones. */}
            <div className="lg:hidden mx-3 mt-1 mb-3 rounded-xl border border-gray-800/70 bg-[#0A0D12]/60 divide-y divide-gray-800/60">
              {/* Invoice ID + live status */}
              <div className="flex items-center justify-between gap-2.5 px-3.5 py-2.5">
                <div className="min-w-0">
                  <p className="text-[9px] text-gray-600 uppercase tracking-widest font-semibold mb-0.5">
                    Invoice ID
                  </p>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-gray-200 text-xs font-mono truncate">
                      {invoice?.invoiceId || invoiceId || "—"}
                    </span>
                    <CopyButton
                      text={invoice?.invoiceId || invoiceId || ""}
                      label="Copy"
                    />
                  </div>
                </div>
                {isPaid ? (
                  <span className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#61CE70]/15 border border-[#61CE70]/30 text-[#61CE70] text-[10px] font-semibold">
                    <CheckCircle2 className="w-3 h-3" /> Paid
                  </span>
                ) : isExpired ? (
                  <span className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-[10px] font-semibold">
                    <AlertCircle className="w-3 h-3" /> Expired
                  </span>
                ) : (
                  <span className="shrink-0 flex mt-4 items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[10px] font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />{" "}
                    Awaiting
                  </span>
                )}
              </div>
              {/* Payment Method */}
              <div className="flex items-center justify-between gap-2.5 px-3.5 py-2.5">
                <p className="text-[9px] text-gray-600 uppercase tracking-widest font-semibold">
                  Payment Method
                </p>
                <span className="flex items-center gap-1.5 text-white text-xs font-medium">
                  {METHOD_ICON[method]}
                  {METHOD_LABELS[method] || method}
                </span>
              </div>
              {/* Network — external-wallet flow only */}
              {method === "wallet" && (
                <div className="flex items-center justify-between gap-2.5 px-3.5 py-2.5">
                  <p className="text-[9px] text-gray-600 uppercase tracking-widest font-semibold">
                    Network
                  </p>
                  <span className="flex items-center gap-1.5 text-white text-xs font-medium">
                    <span className="w-2 h-2 rounded-full bg-blue-400" />
                    Base (USDC)
                  </span>
                </div>
              )}
            </div>

            {/* Mobile-only: "View Payment Status" button — opens the
                StatusTracker as a modal. On desktop the tracker is
                rendered inline in the visual-left column instead. */}
            {!isPaid && !isExpired && (
              <button
                type="button"
                onClick={() => setShowStatusModal(true)}
                className="lg:hidden flex !mt-3 items-center justify-center gap-2 mx-3 mb-2.5 w-[calc(100%-1.5rem)] px-3 py-2 rounded-xl border border-gray-700/60 bg-gray-800/40 hover:border-gray-500 hover:bg-gray-800/60 text-gray-300 hover:text-white transition-all duration-200 cursor-pointer text-xs font-medium"
              >
                <Radio className="w-3.5 h-3.5 text-[#61CE70]" />
                View Payment Status
              </button>
            )}

            {/* Mobile-only trust badges — moved from inside the action
                card to sit at the very bottom of the unified card, below
                the meta box. Desktop renders them inside the action card. */}
            <div className="lg:hidden !mt-3 flex items-center justify-between gap-1.5 px-3 pb-3 pt-1">
              <div className="flex items-center gap-1 flex-1 justify-center">
                <Shield className="w-3 h-3 text-[#61CE70] shrink-0" />
                <span className="text-[9px] min-[400px]:text-[10px] text-gray-400 whitespace-nowrap">
                  Secure Payment
                </span>
              </div>
              <div className="w-px h-3 bg-gray-800" />
              <div className="flex items-center gap-1 flex-1 justify-center">
                <Zap className="w-3 h-3 text-amber-400 shrink-0" />
                <span className="text-[9px] min-[400px]:text-[10px] text-gray-400 whitespace-nowrap">
                  Instant Transfer
                </span>
              </div>
              <div className="w-px h-3 bg-gray-800" />
              <div className="flex items-center gap-1 flex-1 justify-center">
                <Shield className="w-3 h-3 text-blue-400 shrink-0" />
                <span className="text-[9px] min-[400px]:text-[10px] text-gray-400 whitespace-nowrap">
                  Encrypted & Safe
                </span>
              </div>
            </div>

            {/* Mobile-only "Powered by ff.io" — moved here from inside
                the action card so on small screens it sits below the
                trust badges instead of crowding the CTA. Only renders
                when this invoice's method is the Lightning rail; other
                methods don't carry FF attribution. */}
            {method === "lightning-btc" && (
              <div className="md:hidden flex items-center justify-center px-3 pb-3">
                <a
                  href="https://ff.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Visit ff.io"
                  className="inline-flex bg-white rounded-lg px-2 py-1 items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer transition-transform duration-[5000ms] ease-out hover:scale-110"
                >
                  <span>Powered by</span>
                  <img
                    src="/ff-logo.png"
                    alt="ff.io"
                    className="h-6 ml-1 w-auto object-contain"
                  />
                </a>
              </div>
            )}
          </div>

          {/* RIGHT (mobile) / LEFT (desktop): status tracker + trust.
              `lg:order-1` pins this column to the visual left on desktop,
              swapping with the action surface above. On mobile (single
              column) order has no effect, so it still renders below the
              unified card — the section the buyer is actively interacting
              with stays first on small screens. */}
          <div className="space-y-2 sm:space-y-5 lg:space-y-3 lg:order-1">
            {/* Desktop-only invoice summary — same content as the
                mobile in-card version, just rendered as a sibling of
                StatusTracker so on desktop it sits in the visual-left
                column alongside the payment progress instead of
                stacking under the QR/CTA. */}
            <div className="hidden lg:block bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl overflow-hidden">
              <div className="px-4 pt-3 pb-2.5 border-b border-gray-700/50 text-center">
                <p className="text-[9px] text-gray-600 uppercase tracking-widest font-semibold mb-0.5">
                  Amount Due
                </p>
                <p className="text-3xl font-black text-white tracking-tight leading-none">
                  ${amount.toFixed(2)}
                  <span className="text-sm text-gray-500 font-normal ml-1">
                    USD
                  </span>
                </p>
                <div className="inline-flex mt-1.5">
                  {isPaid ? (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#61CE70]/15 border border-[#61CE70]/30 text-[#61CE70] text-[10px] font-semibold">
                      <CheckCircle2 className="w-3 h-3" /> Paid
                    </span>
                  ) : isExpired ? (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-[10px] font-semibold">
                      <AlertCircle className="w-3 h-3" /> Expired
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[10px] font-semibold">
                      <span className="w-1 h-1 rounded-full bg-amber-400 animate-pulse" />{" "}
                      Awaiting Payment
                    </span>
                  )}
                </div>
              </div>
              <div className="px-4 py-2.5 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-xs">Invoice ID</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-300 text-[11px] font-mono">
                      {invoice?.invoiceId || invoiceId || ""}
                    </span>
                    <CopyButton
                      text={invoice?.invoiceId || invoiceId || ""}
                      label="Copy"
                    />
                  </div>
                </div>
                {buyerEmail && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-xs">Your Email</span>
                    <span className="text-white text-xs font-medium truncate ml-2">
                      {buyerEmail}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-xs">Payment Method</span>
                  <span className="flex items-center gap-1.5 text-white text-xs font-medium">
                    {METHOD_ICON[method]}
                    {METHOD_LABELS[method] || method}
                  </span>
                </div>
                {method === "wallet" && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-xs">Network</span>
                    <span className="flex items-center gap-1.5 text-white text-xs">
                      <span className="w-2 h-2 rounded-full bg-blue-400" />
                      Base (USDC)
                    </span>
                  </div>
                )}
              </div>
            </div>

            {!isPaid && !isExpired && (
              <div className="hidden lg:block">
                <StatusTracker
                  steps={buildSteps()}
                  onRefresh={checkStatus}
                  isPolling={isPolling}
                  lastChecked={lastChecked}
                />
              </div>
            )}

            {/* Underpayment disclaimer — hidden on mobile (the compact toast handles it).
                Desktop variant is a tight one-line warning bar so the whole page
                stays under 100vh on a laptop. */}
            {!isPaid && !isExpired && (
              <div className="hidden sm:block bg-amber-500/5 border border-amber-500/30 rounded-3xl lg:rounded-xl p-4 lg:p-2.5">
                <div className="flex items-start lg:items-center gap-3 lg:gap-2">
                  <div className="shrink-0 w-8 h-8 lg:w-6 lg:h-6 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                    <AlertCircle className="w-4 h-4 lg:w-3.5 lg:h-3.5 text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-amber-300 font-semibold text-sm lg:text-[11px] mb-1 lg:mb-0">
                      Pay exactly ${amount.toFixed(2)}
                      <span className="hidden lg:inline text-amber-200/70 font-normal ml-1">
                        — less puts your payment on hold for manual review.
                      </span>
                    </p>
                    <p className="lg:hidden text-amber-200/80 text-[12.5px] leading-relaxed">
                      Paying less than the amount shown puts your payment on
                      hold for manual review, which can take several hours.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Trust card — hidden on mobile (inline badges in payment action replace it)
                and hidden on desktop too (the action card carries the same trust signal
                inline; the trust card here would push the page beyond 100vh). Kept for
                tablet (sm-md), where vertical real-estate is more forgiving. */}
            <div className="hidden sm:block lg:hidden bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-4 h-4 text-[#61CE70]" />
                <span className="text-sm font-semibold text-white">
                  Secure Payment
                </span>
              </div>
              <div className="space-y-3">
                {[
                  {
                    icon: "🔒",
                    text: "Dedicated address for this transaction only",
                  },
                  {
                    icon: "⚡",
                    text: "Funds auto-forwarded after blockchain confirmation",
                  },
                  {
                    icon: "📧",
                    text: "Email confirmation sent to both parties",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 text-sm text-gray-500"
                  >
                    <span className="text-base leading-none mt-0.5">
                      {item.icon}
                    </span>
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center pb-4 space-y-1">
              <p className="text-gray-600 text-xs">
                Having trouble?{" "}
                <Link
                  to="/support"
                  className="text-gray-500 hover:text-gray-300 underline"
                >
                  Contact Support
                </Link>
              </p>
              <p className="text-gray-700 text-xs">
                Powered by CashTap · cashtap.cash
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
